import React from 'react';
import {Box, Button, Chip, IconButton, MenuItem, Stack, TextField, Typography} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {alpha} from '@mui/material/styles';

import {
  CHECK_DEFINITIONS,
  ConditionNode,
  HeaderCheck,
} from '../../headerAnalysisRuleModel';
import {validationErrorTextSx} from '../headerAnalysisRulePresentation';

const conditionControlHeight = 40;

const CONDITION_DETAIL_LABELS: Record<HeaderCheck, string> = {
  spf_result: 'Authentication check',
  spf_alignment: 'Alignment check',
  dkim_result: 'Authentication check',
  dkim_alignment: 'Alignment check',
  dmarc_result: 'Authentication check',
  dmarc_compliance: 'Compliance check',
  arc_result: 'Chain Validation check',
};

interface ConditionRowProps {
  condition: ConditionNode;
  number: string;
  error?: string;
  onDetail: (condition: ConditionNode) => void;
  onRemove: (conditionId: string) => void;
}

export const ConditionRow: React.FC<ConditionRowProps> = ({
  condition, number, error, onDetail, onRemove,
}) => {
  const definition = condition.check ? CHECK_DEFINITIONS[condition.check] : null;
  const detailLabel = condition.check ? CONDITION_DETAIL_LABELS[condition.check] : null;
  const errorId = `condition-error-${condition.id}`;

  return <Box
    data-testid={`condition-row-${condition.id}`}
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: '40px minmax(0, 1fr) 40px',
        sm: '40px minmax(160px, 230px) minmax(120px, 1fr) 40px',
        md: '40px minmax(0, 1fr) 40px',
        lg: '40px minmax(160px, 230px) minmax(120px, 1fr) 40px',
      },
      alignItems: 'start',
      gap: 1,
      minWidth: 0,
    }}
  >
    <Typography sx={{
      gridColumn: 1,
      gridRow: 1,
      color: 'primary.main',
      fontWeight: 700,
      height: conditionControlHeight,
      display: 'flex',
      alignItems: 'center',
    }}>
      {number}
    </Typography>
    <TextField
      select
      size="small"
      value="email_authentication"
      onChange={() => undefined}
      inputProps={{'aria-label': `Email Authentication Check ${number}`}}
      sx={{
        gridColumn: {xs: 2, sm: 'auto', md: 2, lg: 'auto'},
        gridRow: 1,
        width: '100%',
        minWidth: 0,
        bgcolor: 'grey.100',
        '& .MuiSelect-select': {py: 1.25},
        '& .MuiOutlinedInput-root': {
          height: conditionControlHeight,
        },
      }}
    >
      <MenuItem value="email_authentication">Email Authen Check</MenuItem>
    </TextField>
    <Box sx={{
      gridColumn: {xs: '2 / 4', sm: 'auto', md: '2 / 4', lg: 'auto'},
      gridRow: {xs: 2, sm: 1, md: 2, lg: 1},
      minWidth: 0,
    }}>
      <Button
        variant="outlined"
        startIcon={condition.check ? undefined : <AddIcon/>}
        onClick={() => onDetail(condition)}
        aria-label={`Add detail for condition ${number}`}
        aria-describedby={error ? errorId : undefined}
        sx={{
          height: conditionControlHeight,
          minHeight: conditionControlHeight,
          boxSizing: 'border-box',
          width: '100%',
          minWidth: 0,
          justifyContent: 'flex-start',
          color: 'text.primary',
          borderColor: condition.check ? 'divider' : 'primary.main',
          borderStyle: condition.check ? 'solid' : 'dashed',
          bgcolor: condition.check ? 'background.paper' : 'var(--colour-ocean-blue-50, #D9EFF1)',
          px: condition.check ? 1 : 2,
          py: 1,
          '&:hover': {
            borderColor: condition.check ? 'divider' : 'primary.main',
            borderStyle: condition.check ? 'solid' : 'dashed',
            bgcolor: condition.check ? 'grey.50' : 'var(--colour-ocean-blue-50, #D9EFF1)',
          },
        }}
      >
        {condition.check && definition && detailLabel ? (
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{width: '100%', minWidth: 0, overflow: 'hidden'}}>
            <Chip
              label={definition.protocol}
              size="small"
              sx={{bgcolor: alpha('#03A9F4', 0.12), color: '#007AB2', border: '1px solid #81D4FA', flexShrink: 0}}
            />
          <Typography aria-hidden="true">›</Typography>
            <Typography fontWeight={700} noWrap sx={{minWidth: 0}}>{detailLabel}</Typography>
            <Typography aria-hidden="true">=</Typography>
            <Chip label="Fail" color="error" size="small" sx={{height: 22, fontWeight: 700, flexShrink: 0}}/>
          </Stack>
        ) : 'Add Detail'}
      </Button>
      {error && (
        <Typography id={errorId} sx={{...validationErrorTextSx, mt: 0.5}}>
          {error}
        </Typography>
      )}
    </Box>
    <IconButton
      aria-label={`Remove condition ${number}`}
      onClick={() => onRemove(condition.id)}
      sx={{
        gridColumn: {xs: 3, sm: 'auto', md: 3, lg: 'auto'},
        gridRow: 1,
        color: 'primary.dark',
        height: conditionControlHeight,
      }}
    >
      <CloseIcon/>
    </IconButton>
  </Box>;
};
